import React, { useState, useEffect } from 'react';
import './ProspectiveStudent.css';
import DynamicBanner from '../../Pages/include/DynamicBanner';
import api from '../../../api';

const ProspectiveStudent = () => {
  const [prospectiveData, setProspectiveData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeAccordion, setActiveAccordion] = useState(null);
  const [activeFaq, setActiveFaq] = useState(null);

  const formatImage = (img) => {
    if (!img) return null;
    return `${process.env.REACT_APP_API_URL}/public/${img.replace(/^\//, "")}`;
  };

  useEffect(() => {
    const fetchProspectiveData = async () => {
      try {
        const response = await api.get("/api/sitepages/academic/prospective-students");
        const data = response.data.data;

        // Format banner images
        const bannerImagesDesktop = data.banner_section?.desktop_banner_images?.map(img =>
          img ? `/${img.replace(/^\//, "")}` : null
        ).filter(Boolean) || [];

        const bannerImagesMobile = data.banner_section?.mobile_banner_images?.map(img =>
          img ? `/${img.replace(/^\//, "")}` : null
        ).filter(Boolean) || [];

        // Format images
        const studentLifeImage = formatImage(data.student_life_section?.image);
        const admissionsImage = formatImage(data.admissions_section?.admissions?.image);

        // Format FAQ answers
        const formattedFaqList = data.faq_section?.list?.map(faq => ({
          ...faq,
          answer: faq.answer || ""
        })) || [];

        setProspectiveData({
          ...data,
          bannerImagesDesktop,
          bannerImagesMobile,
          studentLifeImage,
          admissionsImage,
          formattedFaqList
        });

        setIsLoading(false);
      } catch (err) {
        console.error("Error fetching Prospective Student data:", err);
        setError("Failed to load Prospective Student data");
        setIsLoading(false);
      }
    };

    fetchProspectiveData();
  }, []);

  const toggleAccordion = (id) => {
    setActiveAccordion(activeAccordion === id ? null : id);
  };

  const toggleFaq = (id) => {
    setActiveFaq(activeFaq === id ? null : id);
  };

  if (isLoading) ;
  if (error || !prospectiveData) return <div>{error}</div>;

  const {
    banner_section,
    student_life_section,
    programs_section,
    admissions_section,
    faq_section,
    bannerImagesDesktop,
    bannerImagesMobile,
    studentLifeImage,
    admissionsImage,
    formattedFaqList
  } = prospectiveData;

  return (
    <>
      {/* ---------------- BANNER ---------------- */}
      {(bannerImagesDesktop.length > 0 || bannerImagesMobile.length > 0) && (
        <DynamicBanner
          title={banner_section?.banner_title || "Prospective Students"}
          bgImage={bannerImagesDesktop}
          bgImageMobile={bannerImagesMobile}
        />
      )}

      <section className="prospective_section">
        <div className="container">
          
          {/* Introduction Section */}
          <div className="row mb-3">
            <div className="sec-title text-center">
              <h2 dangerouslySetInnerHTML={{ __html: banner_section?.heading || "Prospective Students" }} />
            </div>
            {banner_section?.description && (
              <div 
                style={{ whiteSpace: "pre-line", textAlign: "justify" }}
                dangerouslySetInnerHTML={{ __html: banner_section.description }}
              />
            )}
          </div>

          {/* Academic & Research Highlights */}
          <div className="row d-flex justify-content-center  mb-3 mb-md-5">
            {banner_section?.academic_highlights && (
              <div className="col-md-6 col-12">
                 <div className="equal-box ">
                <ul className="visitor-col mb-4 mb-md-0">
                  <h3 className="mb-3 mt-3 text-center" dangerouslySetInnerHTML={{ __html: banner_section.academic_highlights.heading || "Academic Excellence" }} />
                  {banner_section.academic_highlights.items?.map((item, index) => (
                    <li key={index}>
                      <i className="fa fa-dot-circle-o me-2"></i>
                      <span dangerouslySetInnerHTML={{ __html: item }} />
                    </li>
                  ))}
                </ul>
                </div>
              </div>
            )}
            
            {banner_section?.research_highlights && (
              <div className="col-md-6 col-12">
                 <div className="equal-box ">
                <ul className="visitor-col bg-light">
                  <h3 className="mb-3 mt-3 text-center" dangerouslySetInnerHTML={{ __html: banner_section.research_highlights.heading || "Research & Innovation" }} />
                  {banner_section.research_highlights.items?.map((item, index) => (
                    <li key={index}>
                      <i className="fa fa-dot-circle-o me-2"></i>
                      <span dangerouslySetInnerHTML={{ __html: item }} />
                    </li>
                  ))}
                </ul>
                </div>
              </div>
            )}
          </div>

          {/* Student Life & Campus */}
          {student_life_section?.heading && (
            <>
              <div className="sec-title text-center py-3">
                <h2 dangerouslySetInnerHTML={{ __html: student_life_section.heading }} />
              </div>
              <div className="row d-flex justify-content-center align-items-center mb-3 mb-md-5">
                <div className="col-md-8 col-12">
                  {student_life_section.items && student_life_section.items.length > 0 && (
                    <ul className="visitor">
                      {student_life_section.items.map((item, index) => (
                        <li key={index}>
                          <i className="fa fa-check-circle-o me-2"></i>
                          <span dangerouslySetInnerHTML={{ __html: item }} />
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                {studentLifeImage && (
                  <div className="col-md-4 col-12">
                    <img 
                      src={studentLifeImage} 
                      alt="Campus life" 
                      style={{ borderRadius: "10px" }} 
                      className='mt-3 mt-md-0 img-fluid'
                    />
                  </div>
                )}
              </div>
            </>
          )}

          {/* Community Outreach */}
          {student_life_section?.community_outreach && (
            <div className='mb-3 mb-md-5'>
              <div className="sec-title text-center py-3">
                <h2 dangerouslySetInnerHTML={{ __html: student_life_section.community_outreach.heading }} />
              </div>
              {student_life_section.community_outreach.description && (
                <div dangerouslySetInnerHTML={{ __html: student_life_section.community_outreach.description }} />
              )}
            </div>
          )}

          {/* Programs Section */}
          {programs_section && programs_section.length > 0 && (
            <>
              <div className="sec-title text-center py-3">
                <h2>Program Listing</h2>
              </div>
              <div className="accordion mb-5" id="prospecticeAccordion">
                {programs_section.map((programGroup, index) => (
                  <div className="accordion-item" key={index}>
                    <h5 className="accordion-header">
                      <button
                        className={`accordion-button ${activeAccordion === `program-${index}` ? '' : 'collapsed'}`}
                        type="button"
                        onClick={() => toggleAccordion(`program-${index}`)}
                      >
                        <span dangerouslySetInnerHTML={{ __html: programGroup.heading }} />
                      </button>
                    </h5>
                    <div
                      id={`program-${index}`}
                      className={`accordion-collapse collapse ${activeAccordion === `program-${index}` ? 'show' : ''}`}
                    >
                      <div className="accordion-body">
                        <div className="row">
                          {/* Loop through sub_sections inside each program group */}
                          {programGroup.sub_sections && programGroup.sub_sections.map((sub, subIndex) => (
                            <div className="col-md-3 mb-3 col-12" key={subIndex}> 
                              <div className="program-box-card h-100">
                                <div className="program-box mb-2">
                                  <i className="fa fa-user-graduate program-big"></i>
                                  <h4 className="mb-2" dangerouslySetInnerHTML={{ __html: sub.sub_heading }} />
                                  {sub.years && (
                                    <p className="text-secondary program-year">
                                      <span dangerouslySetInnerHTML={{ __html: `(${sub.years})` }} />
                                    </p>
                                  )}
                                </div>
                                
                                {/* Loop through courses */}
                                {sub.courses && sub.courses.length > 0 && (
                                  <div className="program-list ">
                                    {sub.courses.map((course, idx) => (
                                      <div key={idx} className="program-box-links mb-2">
                                        <a 
                                          href={course.link || "#"} 
                                          target="_blank" 
                                          rel="noopener noreferrer"
                                          style={{ display: "block", textDecoration: "none", color: "#333" }}
                                        >
                                          <i className="fa fa-graduation-cap me-2 " style={{color:'var(--thm-bg)'}}></i>
                                          <span dangerouslySetInnerHTML={{ __html: course.name }} />
                                        </a>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Admissions Section */}
          {admissions_section?.admissions && (
            <div className='mb-3 mb-md-5'>
              <div className="sec-title text-center py-3">
                <h2 dangerouslySetInnerHTML={{ __html: admissions_section.admissions.heading }} />
              </div>
              
               {admissions_section.admissions.note_1 && (
                <div dangerouslySetInnerHTML={{ __html: admissions_section.admissions.note_1 }} />
              )}
              
              <div className="row d-flex justify-content-center align-items-center">
                <div className="col-lg-8">

              {admissions_section.admissions.note_2 && (
                <div dangerouslySetInnerHTML={{ __html: admissions_section.admissions.note_2 }} />
              )}
                </div>
                <div className="col-lg-4">
                  {admissionsImage && (
                    
                   
                        <img
                          src={admissionsImage}
                          alt="Admissions"
                          className="img-fluid mb-3 mb-md-0"
                          style={{ 
                            borderRadius: "10px",
                            height: "235px",
                            width: "100%",
                            objectFit: "cover"
                          }}
                        />
                 
                 
                  )}
                </div>
              </div>
             
            </div>
          )}

          {/* Accessible Education */}
          {admissions_section?.accessible_education && (
            <div className='mb-3 mb-md-5'>
              <div className="sec-title text-center py-3">
                <h2 dangerouslySetInnerHTML={{ __html: admissions_section.accessible_education.heading }} />
              </div>
              {admissions_section.accessible_education.note && (
                <div dangerouslySetInnerHTML={{ __html: admissions_section.accessible_education.note }} />
              )}
            </div>
          )}

          {/* Services */}
          {admissions_section?.services && (
            <>
              <div className="sec-title text-center py-3">
                <h2 dangerouslySetInnerHTML={{ __html: admissions_section.services.heading }} />
              </div>
              
              {admissions_section.services.note && (
                <div dangerouslySetInnerHTML={{ __html: admissions_section.services.note }} />
              )}
            </>
          )}

          {/* FAQ Section */}
          {faq_section && formattedFaqList.length > 0 && (
            <>
              <div className="sec-title text-center py-3">
                <h2 dangerouslySetInnerHTML={{ __html: faq_section.heading || "FAQ's" }} />
              </div>

              <div className="accordion mb-5" id="faqAccordion">
                {formattedFaqList.map((faq, index) => (
                  <div className="accordion-item" key={index}>
                    <h5 className="accordion-header">
                      <button
                        className={`accordion-button ${activeFaq === `faq-${index}` ? '' : 'collapsed'}`}
                        type="button"
                        onClick={() => toggleFaq(`faq-${index}`)}
                      >
                        <span dangerouslySetInnerHTML={{ __html: faq.heading }} />
                      </button>
                    </h5>
                    <div
                      id={`faq-${index}`}
                      className={`accordion-collapse collapse ${activeFaq === `faq-${index}` ? 'show' : ''}`}
                    >
                      <div className="accordion-body faq-content">
                        {faq.answer ? (
                          <div dangerouslySetInnerHTML={{ __html: faq.answer }} />
                        ) : (
                          <div dangerouslySetInnerHTML={{ __html: "<p>No answer available</p>" }} />
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </section>
    </>
  );
};

export default ProspectiveStudent;